/**
 * Invoice Settings — tenant-level configuration for tax invoice generation.
 *
 * Drives jurisdiction (heading + tax label + ID label), tax rate, supplier
 * block, payment terms, footer note, and the auto-email/auto-print
 * toggles. Saved settings persist on companies.invoice_settings.
 */
import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { Receipt, FloppyDisk, EnvelopeSimple, Printer, Bell } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { getInvoiceSettings, updateInvoiceSettings } from '../lib/api';

const JURISDICTION_LABELS = {
  au_gst: 'Australia · GST 10% (TAX INVOICE)',
  uk_vat: 'United Kingdom · VAT 20% (VAT INVOICE)',
  nz_gst: 'New Zealand · GST 15% (TAX INVOICE)',
  us_sales: 'United States · Sales Tax (per state)',
  inclusive_flat: 'Tax-inclusive flat (no separate tax line)',
  none: 'No tax',
};

export default function InvoiceSettings() {
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await getInvoiceSettings();
        setSettings(r.data?.settings);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Could not load invoice settings');
      }
    })();
  }, []);

  const update = (k, v) => setSettings((s) => ({ ...s, [k]: v }));

  const save = async () => {
    if (!settings) return;
    setBusy(true);
    try {
      await updateInvoiceSettings({
        jurisdiction: settings.jurisdiction || 'au_gst',
        heading: settings.heading || null,
        tax_label: settings.tax_label || null,
        tax_rate: parseFloat(settings.tax_rate || 0),
        tax_inclusive: !!settings.tax_inclusive,
        supplier_name: settings.supplier_name || null,
        supplier_id_label: settings.supplier_id_label || null,
        supplier_id_value: settings.supplier_id_value || null,
        supplier_address: settings.supplier_address || null,
        supplier_email: settings.supplier_email || null,
        supplier_phone: settings.supplier_phone || null,
        payment_terms: settings.payment_terms || 'Net 14 days',
        footer_note: settings.footer_note || null,
        default_unit_price: parseFloat(settings.default_unit_price || 0),
        freight_taxable: !!settings.freight_taxable,
        auto_email: !!settings.auto_email,
        auto_print: !!settings.auto_print,
        auto_reminder_enabled: !!settings.auto_reminder_enabled,
        auto_reminder_friendly_days: parseInt(settings.auto_reminder_friendly_days ?? 3, 10),
        auto_reminder_firm_days: parseInt(settings.auto_reminder_firm_days ?? 14, 10),
        auto_reminder_final_days: parseInt(settings.auto_reminder_final_days ?? 45, 10),
        auto_reminder_max_sends: parseInt(settings.auto_reminder_max_sends ?? 3, 10),
      });
      toast.success('Invoice settings saved');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  if (!settings) return <Layout><div className="p-8 text-muted-foreground">Loading…</div></Layout>;

  return (
    <Layout>
      <div data-testid="invoice-settings-page" className="p-6 max-w-4xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <Receipt size={26} weight="bold" className="text-violet-600" />
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Tax invoice settings</h1>
            <p className="text-muted-foreground">Generated automatically when an order is packed (draft) and shipped (issued).</p>
          </div>
        </div>

        {/* Jurisdiction */}
        <section className="bg-white border border-zinc-200 p-5 space-y-4">
          <h2 className="text-lg font-bold tracking-tight">Tax jurisdiction</h2>
          <div>
            <Label>Jurisdiction</Label>
            <select
              value={settings.jurisdiction || 'au_gst'}
              onChange={(e) => update('jurisdiction', e.target.value)}
              className="mt-1 block w-full border border-zinc-300 px-3 py-2 text-sm bg-white"
              data-testid="invoice-jurisdiction"
            >
              {Object.entries(JURISDICTION_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
          <div className="grid md:grid-cols-3 gap-3">
            <div>
              <Label>Document heading</Label>
              <Input value={settings.heading || ''} onChange={(e) => update('heading', e.target.value)} placeholder="TAX INVOICE" data-testid="invoice-heading" />
            </div>
            <div>
              <Label>Tax label</Label>
              <Input value={settings.tax_label || ''} onChange={(e) => update('tax_label', e.target.value)} placeholder="GST" data-testid="invoice-tax-label" />
            </div>
            <div>
              <Label>Tax rate (%)</Label>
              <Input type="number" step="0.1" min="0" max="100" value={settings.tax_rate ?? 10} onChange={(e) => update('tax_rate', e.target.value)} data-testid="invoice-tax-rate" />
            </div>
          </div>
          <label className="flex items-center justify-between p-3 bg-zinc-50 border border-zinc-200">
            <div>
              <div className="font-bold text-sm">Tax inclusive pricing</div>
              <div className="text-xs text-muted-foreground">Unit prices already include tax — invoice back-derives the ex-tax line.</div>
            </div>
            <Switch checked={!!settings.tax_inclusive} onCheckedChange={(v) => update('tax_inclusive', v)} data-testid="invoice-tax-inclusive" />
          </label>
          <label className="flex items-center justify-between p-3 bg-zinc-50 border border-zinc-200">
            <div>
              <div className="font-bold text-sm">Charge tax on freight & handling</div>
              <div className="text-xs text-muted-foreground">When ON, freight and handling lines also accrue tax at the rate above.</div>
            </div>
            <Switch checked={!!settings.freight_taxable} onCheckedChange={(v) => update('freight_taxable', v)} data-testid="invoice-freight-taxable" />
          </label>
        </section>

        {/* Supplier block */}
        <section className="bg-white border border-zinc-200 p-5 space-y-3">
          <h2 className="text-lg font-bold tracking-tight">Supplier (your business)</h2>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label>Trading name</Label>
              <Input value={settings.supplier_name || ''} onChange={(e) => update('supplier_name', e.target.value)} placeholder="Acme Co Pty Ltd" data-testid="invoice-supplier-name" />
            </div>
            <div>
              <Label>Tax ID label</Label>
              <Input value={settings.supplier_id_label || ''} onChange={(e) => update('supplier_id_label', e.target.value)} placeholder="ABN" data-testid="invoice-supplier-id-label" />
            </div>
            <div>
              <Label>Tax ID value</Label>
              <Input value={settings.supplier_id_value || ''} onChange={(e) => update('supplier_id_value', e.target.value)} placeholder="12 345 678 901" data-testid="invoice-supplier-id-value" />
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={settings.supplier_phone || ''} onChange={(e) => update('supplier_phone', e.target.value)} placeholder="+61 2 1234 5678" data-testid="invoice-supplier-phone" />
            </div>
            <div className="md:col-span-2">
              <Label>Address</Label>
              <Input value={settings.supplier_address || ''} onChange={(e) => update('supplier_address', e.target.value)} placeholder="42 Elizabeth St, Sydney NSW 2000" data-testid="invoice-supplier-address" />
            </div>
            <div className="md:col-span-2">
              <Label>Email</Label>
              <Input value={settings.supplier_email || ''} onChange={(e) => update('supplier_email', e.target.value)} placeholder="accounts@acme.com" data-testid="invoice-supplier-email" />
            </div>
          </div>
        </section>

        {/* Workflow */}
        <section className="bg-white border border-zinc-200 p-5 space-y-3">
          <h2 className="text-lg font-bold tracking-tight">Workflow</h2>
          <label className="flex items-center justify-between p-3 bg-zinc-50 border border-zinc-200">
            <div className="flex items-start gap-2">
              <EnvelopeSimple size={16} className="mt-0.5" />
              <div>
                <div className="font-bold text-sm">Email invoice on ship</div>
                <div className="text-xs text-muted-foreground">When an order ships and the invoice is finalised, attach the PDF to a customer email automatically.</div>
              </div>
            </div>
            <Switch checked={!!settings.auto_email} onCheckedChange={(v) => update('auto_email', v)} data-testid="invoice-auto-email" />
          </label>
          <label className="flex items-center justify-between p-3 bg-zinc-50 border border-zinc-200">
            <div className="flex items-start gap-2">
              <Printer size={16} className="mt-0.5" />
              <div>
                <div className="font-bold text-sm">Auto-print at packing station</div>
                <div className="text-xs text-muted-foreground">Pop the draft invoice PDF on pack-complete (uses the same printer routing as shipping labels).</div>
              </div>
            </div>
            <Switch checked={!!settings.auto_print} onCheckedChange={(v) => update('auto_print', v)} data-testid="invoice-auto-print" />
          </label>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label>Payment terms</Label>
              <Input value={settings.payment_terms || ''} onChange={(e) => update('payment_terms', e.target.value)} placeholder="Net 14 days" data-testid="invoice-payment-terms" />
            </div>
            <div>
              <Label>Default unit price (fallback)</Label>
              <Input type="number" step="0.01" min="0" value={settings.default_unit_price ?? 0} onChange={(e) => update('default_unit_price', e.target.value)} data-testid="invoice-default-unit-price" />
            </div>
          </div>
          <div>
            <Label>Footer note</Label>
            <Input value={settings.footer_note || ''} onChange={(e) => update('footer_note', e.target.value)} placeholder="Thanks for your business · Terms apply" data-testid="invoice-footer-note" />
          </div>
        </section>

        {/* Auto-reminder scheduler */}
        <section className="bg-white border border-zinc-200 p-5 space-y-3" data-testid="invoice-auto-reminder-section">
          <div className="flex items-center gap-2">
            <Bell size={18} weight="bold" className="text-violet-600" />
            <h2 className="text-lg font-bold tracking-tight">Auto-reminder scheduler</h2>
          </div>
          <p className="text-xs text-muted-foreground">
            Daily background job that emails customers whose invoices are past due.
            Each tone fires at most once per invoice. Manual reminders never count toward the cap.
          </p>
          <label className="flex items-center justify-between p-3 bg-zinc-50 border border-zinc-200">
            <div className="flex items-start gap-2">
              <Bell size={16} className="mt-0.5" />
              <div>
                <div className="font-bold text-sm">Enable auto-reminders</div>
                <div className="text-xs text-muted-foreground">Off by default. Turn on to start the daily 09:00 UTC sweep.</div>
              </div>
            </div>
            <Switch
              checked={!!settings.auto_reminder_enabled}
              onCheckedChange={(v) => update('auto_reminder_enabled', v)}
              data-testid="invoice-auto-reminder-enabled"
            />
          </label>
          <div className="grid md:grid-cols-4 gap-3 opacity-100">
            <div>
              <Label>Friendly @ (days overdue)</Label>
              <Input
                type="number" min="0" max="365"
                value={settings.auto_reminder_friendly_days ?? 3}
                onChange={(e) => update('auto_reminder_friendly_days', e.target.value)}
                data-testid="invoice-auto-reminder-friendly-days"
                disabled={!settings.auto_reminder_enabled}
              />
            </div>
            <div>
              <Label>Firm @ (days overdue)</Label>
              <Input
                type="number" min="1" max="365"
                value={settings.auto_reminder_firm_days ?? 14}
                onChange={(e) => update('auto_reminder_firm_days', e.target.value)}
                data-testid="invoice-auto-reminder-firm-days"
                disabled={!settings.auto_reminder_enabled}
              />
            </div>
            <div>
              <Label>Final notice @ (days overdue)</Label>
              <Input
                type="number" min="1" max="365"
                value={settings.auto_reminder_final_days ?? 45}
                onChange={(e) => update('auto_reminder_final_days', e.target.value)}
                data-testid="invoice-auto-reminder-final-days"
                disabled={!settings.auto_reminder_enabled}
              />
            </div>
            <div>
              <Label>Max auto-sends per invoice</Label>
              <Input
                type="number" min="1" max="10"
                value={settings.auto_reminder_max_sends ?? 3}
                onChange={(e) => update('auto_reminder_max_sends', e.target.value)}
                data-testid="invoice-auto-reminder-max-sends"
                disabled={!settings.auto_reminder_enabled}
              />
            </div>
          </div>
          <p className="text-[11px] text-zinc-500">
            Tip: set a customer or order's <code>do_not_auto_remind</code> flag in the database to exclude VIPs (manual reminders still work for them).
          </p>
        </section>

        <div className="flex justify-end">
          <Button onClick={save} disabled={busy} className="bg-violet-600 hover:bg-violet-700" data-testid="invoice-settings-save">
            <FloppyDisk size={16} className="mr-1" weight="bold" /> {busy ? 'Saving…' : 'Save settings'}
          </Button>
        </div>
      </div>
    </Layout>
  );
}
